import {
  VIDEO_PLAYBACK_FORBIDDEN_RESPONSE,
  type VideoPlaybackResponse,
  type VideoSelection,
} from "@meta-video/contracts";
import { Alert, Button, Group, Loader, Stack, Text } from "@mantine/core";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { SessionExpiredError } from "../../shared/api/client.js";
import {
  useAnalytics,
  useAnalyticsSessionId,
} from "../../shared/analytics/AnalyticsProvider.js";
import { analyticsEvents } from "../../shared/analytics/events.js";
import {
  VideoPlaybackApiError,
  type VideoApi,
  videoApi,
} from "./api.js";
import { StatusBadge } from "./StatusBadge.js";

type Fetcher = typeof fetch;
type PlayerPhase = "error" | "idle" | "preparing" | "ready" | "status";

const MAXIMUM_AUTOMATIC_RENEWALS_PER_PLAYER = 3;

interface RestorePoint {
  readonly currentTime: number;
  readonly shouldPlay: boolean;
}

export interface VideoPlayerProps {
  readonly api?: VideoApi;
  readonly autoPrepare?: boolean;
  /** False when the selection is not a real brand video (the creative report plays ads by ad ID). */
  readonly capturePlayback?: boolean;
  readonly fetcher?: Fetcher;
  readonly label: string;
  readonly onAccessChanged?: () => void;
  readonly onSessionExpired?: () => void;
  /** Image shown before playback, and instead of it when the video isn't stored. */
  readonly poster?: string | null;
  readonly probeTimeoutMs?: number;
  readonly selection: VideoSelection;
  readonly surface?: "comparison" | "detail" | "report";
}

const runtimeFetch: Fetcher = (input, init) => fetch(input, init);

function genericPlaybackMessage(error: unknown): string {
  if (
    error instanceof VideoPlaybackApiError
    && error.code === "VIDEO_PLAYBACK_FORBIDDEN"
  ) {
    return `${VIDEO_PLAYBACK_FORBIDDEN_RESPONSE.message}.`;
  }
  return "Video could not be prepared. Try again.";
}

function isValidRangeResponse(response: Response): boolean {
  if (response.type !== "cors" || response.status !== 206) return false;
  if (response.headers.get("Content-Length") !== "1") return false;
  const contentRange = response.headers.get("Content-Range");
  if (contentRange === null) return false;
  const matched = /^bytes 0-0\/([1-9][0-9]*)$/u.exec(contentRange);
  return matched !== null && Number(matched[1]) >= 1;
}

async function cancelProbeBody(response: Response): Promise<void> {
  try {
    await response.body?.cancel();
  } catch {
    // The one-byte probe has already served its purpose. Body cancellation is
    // best-effort because browsers may report it as consumed.
  }
}

async function readExactlyOneProbeByte(response: Response): Promise<boolean> {
  if (response.body === null) return false;
  const reader = response.body.getReader();
  let byteCount = 0;
  try {
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) return byteCount === 1;
      byteCount += chunk.value.byteLength;
      if (byteCount > 1) {
        try {
          await reader.cancel();
        } catch {
          // The response is already invalid; cancellation is best-effort.
        }
        return false;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export function VideoPlayer({
  api = videoApi,
  autoPrepare = false,
  capturePlayback = true,
  fetcher = runtimeFetch,
  label,
  onAccessChanged,
  onSessionExpired,
  poster,
  probeTimeoutMs = 10_000,
  selection,
  surface = "report",
}: VideoPlayerProps) {
  const analytics = useAnalytics();
  const analyticsSessionId = useAnalyticsSessionId();
  const [phase, setPhase] = useState<PlayerPhase>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [assetStatus, setAssetStatus] = useState<Exclude<VideoPlaybackResponse["status"], "hosted"> | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mountedRef = useRef(true);
  const phaseRef = useRef<PlayerPhase>("idle");
  const operationRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const retryUsedRef = useRef(false);
  const renewalCountRef = useRef(0);
  const sourceGenerationRef = useRef(0);
  const playIntentRef = useRef(false);
  const restorePointRef = useRef<RestorePoint | null>(null);
  const automaticPreparationSelectionRef = useRef<string | null>(null);

  const transition = useCallback((next: PlayerPhase) => {
    phaseRef.current = next;
    if (mountedRef.current) setPhase(next);
  }, []);

  const clearSource = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const hadSource = video.hasAttribute("src")
      || video.hasAttribute("data-source-generation");
    video.removeAttribute("src");
    video.removeAttribute("data-source-generation");
    if (hadSource) video.load();
  }, []);

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      phaseRef.current = "idle";
      automaticPreparationSelectionRef.current = null;
      operationRef.current += 1;
      controllerRef.current?.abort();
      controllerRef.current = null;
      clearSource();
    };
  }, [clearSource]);

  const prepare = useCallback(async (
    restorePoint: RestorePoint,
    retryAlreadyConsumed: boolean,
  ) => {
    if (phaseRef.current === "preparing") return;
    controllerRef.current?.abort();
    let controller = new AbortController();
    controllerRef.current = controller;
    const operation = ++operationRef.current;
    retryUsedRef.current = retryAlreadyConsumed;
    let forceRefresh = retryAlreadyConsumed;
    restorePointRef.current = restorePoint;
    setErrorMessage(null);
    setAssetStatus(null);
    transition("preparing");

    const isOwned = () => mountedRef.current && operationRef.current === operation;
    const isCurrent = () => isOwned() && !controller.signal.aborted;

    const fail = (error: unknown) => {
      if (!isOwned()) return;
      if (
        error instanceof VideoPlaybackApiError
        && error.code === "VIDEO_PLAYBACK_FORBIDDEN"
      ) onAccessChanged?.();
      if (error instanceof SessionExpiredError) onSessionExpired?.();
      setErrorMessage(genericPlaybackMessage(error));
      transition("error");
    };

    while (isCurrent()) {
      let playback: VideoPlaybackResponse;
      try {
        playback = await api.getPlaybackUrl(selection, {
          forceRefresh,
          signal: controller.signal,
        });
      } catch (error) {
        if (!isCurrent()) return;
        fail(error);
        return;
      }
      if (!isCurrent()) return;
      if (playback.status !== "hosted") {
        clearSource();
        setAssetStatus(playback.status);
        transition("status");
        return;
      }

      let response: Response | null = null;
      let retryableFailure = false;
      let validRangeResponse = false;
      let timeout: number | undefined;
      let timedOut = false;
      try {
        timeout = window.setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, probeTimeoutMs);
        response = await fetcher(playback.signedUrl, {
          cache: "no-store",
          credentials: "omit",
          headers: { Range: "bytes=0-0" },
          method: "GET",
          redirect: "error",
          signal: controller.signal,
        });
        retryableFailure = response.status === 403 || response.type === "opaque";
        if (!retryableFailure && isValidRangeResponse(response)) {
          validRangeResponse = await readExactlyOneProbeByte(response);
        }
      } catch (error) {
        if (!isOwned()) return;
        if (controller.signal.aborted && !timedOut) return;
        retryableFailure = true;
      } finally {
        if (timeout !== undefined) window.clearTimeout(timeout);
      }

      if (response && !validRangeResponse) await cancelProbeBody(response);
      if (!isOwned()) return;

      if (retryableFailure) {
        if (
          retryUsedRef.current
          || renewalCountRef.current >= MAXIMUM_AUTOMATIC_RENEWALS_PER_PLAYER
        ) {
          fail(new Error("Video probe failed twice"));
          return;
        }
        retryUsedRef.current = true;
        renewalCountRef.current += 1;
        forceRefresh = true;
        if (controller.signal.aborted) {
          controllerRef.current = new AbortController();
          // A timeout aborts its controller. Continue the same logical
          // operation with a fresh controller for the one permitted retry.
          controller = controllerRef.current;
        }
        continue;
      }

      if (response === null || !validRangeResponse) {
        fail(new Error("Video range response was invalid"));
        return;
      }
      if (!isCurrent()) return;

      const video = videoRef.current;
      if (!video) return;
      sourceGenerationRef.current += 1;
      clearSource();
      video.src = playback.signedUrl;
      video.dataset.sourceGeneration = String(sourceGenerationRef.current);
      video.load();
      transition("ready");
      return;
    }
  }, [api, clearSource, fetcher, onAccessChanged, onSessionExpired, probeTimeoutMs, selection, transition]);

  const beginPreparation = useCallback((shouldPlay: boolean) => {
    if (phaseRef.current === "preparing") return;
    playIntentRef.current = shouldPlay;
    retryUsedRef.current = false;
    void prepare({ currentTime: 0, shouldPlay }, false);
  }, [prepare]);

  const play = useCallback(() => beginPreparation(true), [beginPreparation]);

  useEffect(() => {
    if (!autoPrepare) {
      automaticPreparationSelectionRef.current = null;
      return;
    }
    const selectionKey = JSON.stringify([selection.brandId, selection.videoId]);
    if (automaticPreparationSelectionRef.current === selectionKey) return;
    automaticPreparationSelectionRef.current = selectionKey;
    beginPreparation(false);
  }, [autoPrepare, beginPreparation, selection.brandId, selection.videoId]);

  const recoverFromMediaError = useCallback(() => {
    if (phaseRef.current !== "ready") return;
    if (
      retryUsedRef.current
      || renewalCountRef.current >= MAXIMUM_AUTOMATIC_RENEWALS_PER_PLAYER
    ) {
      setErrorMessage("Video could not be prepared. Try again.");
      transition("error");
      return;
    }
    renewalCountRef.current += 1;
    const video = videoRef.current;
    const restorePoint = {
      currentTime: Number.isFinite(video?.currentTime) ? video?.currentTime ?? 0 : 0,
      shouldPlay: playIntentRef.current,
    };
    void prepare(restorePoint, true);
  }, [prepare, transition]);

  const restorePlayback = useCallback(() => {
    const video = videoRef.current;
    const restorePoint = restorePointRef.current;
    if (!video || !restorePoint) return;
    try {
      video.currentTime = restorePoint.currentTime;
    } catch {
      // Some media implementations reject a seek until duration is known.
    }
    if (restorePoint.shouldPlay) void video.play().catch(() => undefined);
  }, []);

  const markNativePlaybackSuccess = useCallback(() => {
    if (phaseRef.current === "ready") retryUsedRef.current = false;
  }, []);

  return (
    <Stack className="video-player" gap="sm">
      <video
        aria-label={label}
        controls
        onCanPlay={markNativePlaybackSuccess}
        onError={recoverFromMediaError}
        onLoadedMetadata={restorePlayback}
        onPause={() => {
          if (phaseRef.current === "ready") playIntentRef.current = false;
        }}
        onPlay={() => {
          if (phaseRef.current !== "ready") return;
          playIntentRef.current = true;
          if (analyticsSessionId && capturePlayback) {
            analytics.capture(
              analyticsEvents.playbackStarted(analyticsSessionId, {
                brandId: selection.brandId,
                surface,
                videoId: selection.videoId,
              }),
            );
          }
        }}
        onPlaying={markNativePlaybackSuccess}
        poster={poster ?? undefined}
        preload="none"
        ref={videoRef}
      />
      {phase === "idle" ? (
        <Button onClick={play} type="button">Play {label}</Button>
      ) : null}
      {phase === "preparing" ? (
        <Group aria-live="polite" role="status">
          <Loader size="xs" />
          <Text size="sm">Preparing video…</Text>
        </Group>
      ) : null}
      {phase === "ready" ? (
        <Text aria-live="polite" c="dimmed" role="status" size="sm">Video ready to play.</Text>
      ) : null}
      {phase === "status" && assetStatus ? (
        <span aria-live="polite" role="status">
          <StatusBadge status={assetStatus} />
        </span>
      ) : null}
      {phase === "error" && errorMessage ? (
        <Alert color="red" role="alert">
          <Stack gap="xs">
            <Text size="sm">{errorMessage}</Text>
            <Button onClick={play} size="compact-sm" type="button" variant="light">Try video again</Button>
          </Stack>
        </Alert>
      ) : null}
    </Stack>
  );
}
